import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const GameLicenseForm = ({ initialUserId, onLicenseCreated }) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(initialUserId || "");
  const [selectedGame, setSelectedGame] = useState("lsraware");
  const [selectedDevice, setSelectedDevice] = useState("PC");
  const [gamePlay, setGamePlay] = useState("10");
  const [expirationDate, setExpirationDate] = useState("");
  const [assignedEmail, setAssignedEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const gameOptions = [
    { label: "Life Saving Rules Aware", value: "lsraware" },
    { label: "Life Saving Rules Pro", value: "lsrpro" },
    { label: "Life Saving Rules Road", value: "lsrroad" },
    { label: "Driving Aware All Roads", value: "drivingaware" },
    { label: "Driving Pro Blacktop", value: "blacktopdriving" },
    { label: "Driving Pro Graded Road", value: "gradedroaddriving" },
    { label: "Process Safety", value: "processsafety" },
  ];

  const deviceTypes = ["PC", "VR"];

  // Fetch registered users list
  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialUserId) {
      setSelectedUser(initialUserId);
    }
  }, [initialUserId]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/admin/users`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch users");
      }

      const data = await response.json();
      setUsers(data);
      if (data.length > 0 && !selectedUser) {
        setSelectedUser(data[0].id);
      }
    } catch (err) {
      console.error("Error loading users:", err);
      setMessage({ type: "error", text: "Failed to load user list" });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!selectedUser) {
      setMessage({ type: "error", text: "Please select a target user" });
      return;
    }

    if (!selectedGame) {
      setMessage({ type: "error", text: "Please select a game" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        userId: selectedUser,
        gameName: selectedGame,
        type: selectedDevice,
        totalPlays: parseInt(gamePlay, 10) || 10,
        expirationDate: expirationDate || null,
        assignedEmail: assignedEmail || undefined,
      };

      const response = await fetch(`${BACKEND_URL}/admin/licenses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to generate license");
      }

      setMessage({
        type: "success",
        text: `License generated successfully! PIN Code: ${result.license.licenseCode}`,
      });

      setGamePlay("10");
      setExpirationDate("");
      setAssignedEmail("");

      if (onLicenseCreated) {
        onLicenseCreated(result.license);
      }
    } catch (err) {
      console.error("Error creating license:", err);
      setMessage({ type: "error", text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p style={{ textAlign: "center", padding: "20px" }}>Loading users...</p>;

  return (
    <div className="container-admin" style={{ maxWidth: "600px", margin: "0 auto" }}>
      <h1 className="title-blue">Generate License Manually</h1>
      <p style={{ color: "#666", marginBottom: "20px" }}>
        Select a user and game to manually issue a new license PIN code.
      </p>

      {message.text && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "6px",
            marginBottom: "20px",
            backgroundColor: message.type === "error" ? "#fde8e8" : "#e6fffa",
            color: message.type === "error" ? "#e53e3e" : "#234e52",
            border: `1px solid ${message.type === "error" ? "#feb2b2" : "#b2f5ea"}`,
            fontWeight: message.type === "success" ? "bold" : "normal",
          }}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <label className="label-admin">Select Target User *</label>
        <select
          className="select"
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          required
        >
          <option value="">-- Choose a User --</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.firstName} {user.lastName} ({user.email}) {user.company ? `[${user.company}]` : ""} {!user.active ? "(INACTIVE)" : ""}
            </option>
          ))}
        </select>

        <label className="label-admin" style={{ marginTop: "15px" }}>
          Target Game Module *
        </label>
        <select
          className="select"
          value={selectedGame}
          onChange={(e) => setSelectedGame(e.target.value)}
          required
        >
          {gameOptions.map((game) => (
            <option key={game.value} value={game.value}>
              {game.label}
            </option>
          ))}
        </select>

        <label className="label-admin" style={{ marginTop: "15px" }}>
          Platform / Device Type
        </label>
        <select
          className="select"
          value={selectedDevice}
          onChange={(e) => setSelectedDevice(e.target.value)}
        >
          {deviceTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>

        <label className="label-admin" style={{ marginTop: "15px" }}>
          Maximum Plays Allowed
        </label>
        <input
          className="input"
          type="number"
          min="1"
          placeholder="e.g. 10"
          value={gamePlay}
          onChange={(e) => setGamePlay(e.target.value)}
        />

        <label className="label-admin" style={{ marginTop: "15px" }}>
          Expiration Date (Optional - Defaults to 1 Year)
        </label>
        <input
          className="input"
          type="date"
          value={expirationDate}
          onChange={(e) => setExpirationDate(e.target.value)}
        />

        <label className="label-admin" style={{ marginTop: "15px" }}>
          Assigned Player Email (Optional)
        </label>
        <input
          className="input"
          type="email"
          placeholder="Defaults to purchaser email"
          value={assignedEmail}
          onChange={(e) => setAssignedEmail(e.target.value)}
        />

        <button
          type="submit"
          className="button"
          disabled={submitting}
          style={{ marginTop: "25px", width: "100%" }}
        >
          {submitting ? "Generating License..." : "Generate & Assign License"}
        </button>
      </form>
    </div>
  );
};

export default GameLicenseForm;